import React, { useState, useEffect } from 'react';
import { updateRaciMatrix, updateSlaConfig, fetchRaciMatrix, fetchSlaConfig, runMonthlyFlagsTickets } from '../../api';
import CountryPicker from '../../CountryPicker';

// Map organizations per tier (must match backend) — hoisted to module scope
const ORGS_BY_TIER = {
  level1: ['unicefCo', 'epi'],
  level2: ['moh', 'unicefRo'],
  level3: ['unicefPd', 'unicefSd', 'gavi']
};

const TicketAdminTab = () => {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const initialRaciMatrix = React.useMemo(() => ({
    unicefCo: '',
    epi: '',
    moh: '',
    unicefRo: '',
    unicefPd: '',
    unicefSd: '',
    gavi: ''
  }), []);

  const [raciMatrix, setRaciMatrix] = useState(initialRaciMatrix);

  

  const parseEmails = (s) => String(s || '').split(/[;,\n]/).map(x => x.trim()).filter(Boolean);
  const joinEmails = (arr) => (Array.isArray(arr) ? arr.join(', ') : '');

  const initialSlaConfig = React.useMemo(() => ({
    low: { response: 144, resolution: 288 },
    medium: { response: 72, resolution: 144 },
    high: { response: 48, resolution: 72 },
    critical: { response: 24, resolution: 48 }
  }), []);

  const [slaConfig, setSlaConfig] = useState(initialSlaConfig);

  const [loading, setLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [runError, setRunError] = useState('');
  const [runOutput, setRunOutput] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadConfigurations = React.useCallback(async () => {
    try {
      setLoading(true);
      const iso3 = (selectedCountry?.iso3 || selectedCountry?.value || '').toUpperCase();
      const [raciData, slaData] = await Promise.all([
        fetchRaciMatrix(iso3).catch(() => null),
        fetchSlaConfig(iso3).catch(() => null)
      ]);
      if (raciData) {
        if (raciData.tiers) {
          const tiers = raciData.tiers;
          const next = {};
          // Derive org fields from tiers JSON for display
          ORGS_BY_TIER.level1.forEach(org => { next[org] = joinEmails(tiers?.level1?.[org] || []); });
          ORGS_BY_TIER.level2.forEach(org => { next[org] = joinEmails(tiers?.level2?.[org] || []); });
          ORGS_BY_TIER.level3.forEach(org => { next[org] = joinEmails(tiers?.level3?.[org] || []); });
          setRaciMatrix(prev => ({ ...prev, ...next }));
        } else {
          setRaciMatrix(prev => ({ ...prev, ...raciData }));
        }
      }
      if (slaData) setSlaConfig(slaData);
      setMessage({ type: '', text: '' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load configurations' });
    } finally {
      setLoading(false);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedCountry) {
      // Reset local state when switching countries to avoid showing stale values
      setRaciMatrix(initialRaciMatrix);
      setSlaConfig(initialSlaConfig);
      loadConfigurations();
    }
  }, [selectedCountry, initialRaciMatrix, initialSlaConfig, loadConfigurations]);

  

  const handleRaciChange = (field, value) => {
    setRaciMatrix(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSlaChange = (priority, field, value) => {
    setSlaConfig(prev => ({
      ...prev,
      [priority]: {
        ...prev[priority],
        [field]: parseInt(value)
      }
    }));
  };

  const handleSaveRaci = async () => {
    try {
      setLoading(true);
      const iso3 = selectedCountry?.iso3 || selectedCountry?.value;
      if (!iso3) {
        setMessage({ type: 'error', text: 'Please select a country first' });
        return;
      }
      // Build tiers JSON from current org inputs
      const tiers = {
        level1: {
          unicefCo: parseEmails(raciMatrix.unicefCo),
          epi: parseEmails(raciMatrix.epi)
        },
        level2: {
          moh: parseEmails(raciMatrix.moh),
          unicefRo: parseEmails(raciMatrix.unicefRo)
        },
        level3: {
          unicefPd: parseEmails(raciMatrix.unicefPd),
          unicefSd: parseEmails(raciMatrix.unicefSd),
          gavi: parseEmails(raciMatrix.gavi)
        }
      };
      await updateRaciMatrix(String(iso3).toUpperCase(), { tiers });
      setMessage({ type: 'success', text: 'RACI Matrix updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update RACI Matrix' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSla = async () => {
    try {
      setLoading(true);
      const iso3 = selectedCountry?.iso3 || selectedCountry?.value;
      if (!iso3) {
        setMessage({ type: 'error', text: 'Please select a country first' });
        return;
      }
      await updateSlaConfig(String(iso3).toUpperCase(), slaConfig);
      setMessage({ type: 'success', text: 'SLA configuration updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update SLA configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleRunMonthlyFlagsTickets = async () => {
    try {
      setRunLoading(true);
      setRunError('');
      const result = await runMonthlyFlagsTickets();
      setRunOutput(result);
    } catch (error) {
      setRunError(error?.message || 'Failed to run monthly flags tickets');
      setRunOutput(null);
    } finally {
      setRunLoading(false);
    }
  };

    return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Ticket System Administration</h2>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold">Monthly Flags Tickets Run</h3>
            <p className="text-sm text-gray-600 mt-1">
              Runs `backend_monthlyFlagsTickets` and shows the raw response output.
            </p>
          </div>
          <button
            onClick={handleRunMonthlyFlagsTickets}
            disabled={runLoading}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {runLoading ? 'Running...' : 'Run Now'}
          </button>
        </div>

        {runError && (
          <div className="mt-4 p-3 rounded bg-red-100 text-red-700 text-sm">
            {runError}
          </div>
        )}

        {runOutput && (
          <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Output</p>
            <pre className="text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-auto max-h-96">
              {JSON.stringify(runOutput, null, 2)}
            </pre>
          </div>
        )}
      </div>

      <div className="w-64 mb-6">
        <CountryPicker
          value={selectedCountry}
          onChange={setSelectedCountry}
          placeholder="Select a country..."
        />
      </div>

      {selectedCountry && (
        <>
          {message.text && (
            <div className={`mb-4 p-4 rounded ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {message.text}
            </div>
          )}

          {/* RACI Escalation Tiers Section */}
          <div className="bg-white shadow-md rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold mb-4">RACI Escalation Tiers</h3>

            {/* Tier 1 */}
            <div className="border-2 border-green-400 rounded-lg p-4 mb-4">
              <div className="mb-2">
                <h4 className="text-md font-medium">Tier 1</h4>
                <p className="text-sm text-gray-600">Initial Assigned Officer; notified on ticket creation and before response deadline.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UNICEF Country Office (example)</label>
                  <input
                    type="text"
                    value={raciMatrix.unicefCo}
                    onChange={(e) => handleRaciChange('unicefCo', e.target.value)}
                    className="w-full p-2 border rounded-md"
                    placeholder="Comma-separated emails"
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: email1@org.org, email2@org.org</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">EPI</label>
                  <input
                    type="text"
                    value={raciMatrix.epi}
                    onChange={(e) => handleRaciChange('epi', e.target.value)}
                    className="w-full p-2 border rounded-md"
                    placeholder="Comma-separated emails"
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: email1@org.org, email2@org.org</p>
                </div>
              </div>
            </div>

            {/* Tier 2 */}
            <div className="border-2 border-yellow-400 rounded-lg p-4 mb-4">
              <div className="mb-2">
                <h4 className="text-md font-medium">Tier 2</h4>
                <p className="text-sm text-gray-600">Escalation if no response by the response deadline.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">NLWG / MOH</label>
                  <input
                    type="text"
                    value={raciMatrix.moh}
                    onChange={(e) => handleRaciChange('moh', e.target.value)}
                    className="w-full p-2 border rounded-md"
                    placeholder="Comma-separated emails"
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: email1@org.org, email2@org.org</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UNICEF RO</label>
                  <input
                    type="text"
                    value={raciMatrix.unicefRo}
                    onChange={(e) => handleRaciChange('unicefRo', e.target.value)}
                    className="w-full p-2 border rounded-md"
                    placeholder="Comma-separated emails"
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: email1@org.org, email2@org.org</p>
                </div>
              </div>
            </div>

            {/* Tier 3 */}
            <div className="border-2 border-red-400 rounded-lg p-4">
              <div className="mb-2">
                <h4 className="text-md font-medium">Tier 3</h4>
                <p className="text-sm text-gray-600">Escalation if not resolved by the resolution deadline.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UNICEF PG</label>
                  <input
                    type="text"
                    value={raciMatrix.unicefPd}
                    onChange={(e) => handleRaciChange('unicefPd', e.target.value)}
                    className="w-full p-2 border rounded-md"
                    placeholder="Comma-separated emails"
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: email1@org.org, email2@org.org</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">UNICEF SD</label>
                  <input
                    type="text"
                    value={raciMatrix.unicefSd}
                    onChange={(e) => handleRaciChange('unicefSd', e.target.value)}
                    className="w-full p-2 border rounded-md"
                    placeholder="Comma-separated emails"
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: email1@org.org, email2@org.org</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GAVI</label>
                  <input
                    type="text"
                    value={raciMatrix.gavi}
                    onChange={(e) => handleRaciChange('gavi', e.target.value)}
                    className="w-full p-2 border rounded-md"
                    placeholder="Comma-separated emails"
                  />
                  <p className="text-xs text-gray-500 mt-1">Example: email1@org.org, email2@org.org</p>
                </div>
              </div>
            </div>

            <button
              onClick={handleSaveRaci}
              disabled={loading}
              className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save RACI Matrix'}
            </button>
          </div>

          {/* SLA Configuration Section */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">SLA Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(slaConfig).map(([priority, config]) => (
                <div key={priority} className="border rounded-lg p-4">
                  <h4 className="text-md font-medium mb-3 capitalize">{priority} Priority</h4>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Response Time (hours)</label>
                      <input
                        type="number"
                        value={config.response}
                        onChange={(e) => handleSlaChange(priority, 'response', e.target.value)}
                        className="w-full p-2 border rounded-md"
                        min="1"
                      />
                      <div className="text-xs text-gray-500 mt-1">{(Number(config.response) / 24).toFixed(1)} days</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Resolution Time (hours)</label>
                      <input
                        type="number"
                        value={config.resolution}
                        onChange={(e) => handleSlaChange(priority, 'resolution', e.target.value)}
                        className="w-full p-2 border rounded-md"
                        min="1"
                      />
                      <div className="text-xs text-gray-500 mt-1">{(Number(config.resolution) / 24).toFixed(1)} days</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={handleSaveSla}
              disabled={loading}
              className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save SLA Configuration'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TicketAdminTab; 